"use client";

import { createContext, useContext, useEffect, useId, useMemo, useRef, useState, type CSSProperties, type HTMLAttributes, type ReactElement, type ReactNode } from "react";
import { Popover as PopoverPrimitive } from "@base-ui/react/popover";
import { useRender } from "@base-ui/react/use-render";
import { motion, useMotionValue } from "motion/react";
import { CheckIcon, CopyIcon, XIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTitle, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useShape } from "@/lib/shape-context";
import styles from "./tooltip.module.css";

export const TOOLTIP_SHOW_DELAY = 150;
export const TOOLTIP_LOCK_DELAY = 1500;
const timing = { "--tooltip-lock-delay": `${TOOLTIP_LOCK_DELAY}ms` } as CSSProperties;

type TooltipPhase = "closed" | "preview" | "locked";
type TooltipSide = "top" | "right" | "bottom" | "left";
type ActiveTooltip = { id: string; locked: boolean; close: () => void };
function useTooltipGroup() {
  const active = useRef<ActiveTooltip | null>(null);
  return useMemo(() => ({
    get: () => active.current,
    set: (value: ActiveTooltip | null) => { active.current = value; },
  }), []);
}
const TooltipGroupContext = createContext<ReturnType<typeof useTooltipGroup> | null>(null);
const TooltipDepthContext = createContext(0);
const TooltipPortalContainerContext = createContext<HTMLElement | null>(null);

interface TooltipProviderProps { children: ReactNode }

/** One root chain at a time. Nested content gets its own sibling group. */
function TooltipProvider({ children }: TooltipProviderProps) {
  const group = useTooltipGroup();
  return <TooltipGroupContext.Provider value={group}>{children}</TooltipGroupContext.Provider>;
}

function TooltipPortalContainer({ value, children }: { value: HTMLElement | null; children: ReactNode }) {
  return <TooltipPortalContainerContext.Provider value={value}>{children}</TooltipPortalContainerContext.Provider>;
}

// Let composed actions (links, menu triggers, disabled buttons) own their semantics.
function TooltipTarget({ element, isTerm, ref, ...props }: React.ComponentPropsWithRef<"button"> & { element: ReactElement; isTerm: boolean }) {
  const targetProps: Record<string, unknown> = { ...props };
  if (!isTerm) {
    for (const key of ["aria-haspopup", "aria-expanded", "aria-controls", "role", "data-open", "data-closed", "data-popup-open", "type"]) delete targetProps[key];
    // The wrapped control owns activation; Popover's handlers can suppress
    // composite controls (such as toggles) or synthesize an extra link click.
    delete targetProps.onKeyDown;
    delete targetProps.onKeyUp;
    if (targetProps.tabIndex === undefined) delete targetProps.tabIndex;
  }
  return useRender({ render: element, ref, props: targetProps });
}

interface TooltipProps {
  content: ReactNode;
  children: ReactElement;
  title?: string;
  side?: TooltipSide;
  sideOffset?: number;
  className?: string;
  contentClassName?: string;
  /** Match the rendered trigger, as with Base UI's render prop. */
  nativeButton?: boolean;
  /** Actions keep their click behavior; terms use click/tap/Enter to lock. */
  trigger?: "action" | "term";
  forceOpen?: boolean;
  followCursor?: "x" | "y";
  onOpenChange?: (open: boolean) => void;
}

function TooltipLockIcon({ locked }: { locked: boolean }) {
  return (
    <svg className={styles.lockIcon} data-locked={locked} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
      <path className={styles.shackle} d="M7 11V7a5 5 0 0 1 10 0v4" />
      <rect x="4" y="11" width="16" height="11" rx="3" />
      <path d="M12 15v3" />
    </svg>
  );
}

/** Copy the raw value, including line breaks, without leaving the locked panel. */
function TooltipCopyValue({ value, label, children }: { value: string; label: string; children?: ReactNode }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(timer);
  }, [copied]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      setCopied(false);
      toast.error(`Could not copy ${label}. Select the text and copy it manually.`);
    }
  }

  return (
    <div className={styles.copyValue}>
      <span className={styles.copyText}>{children ?? value}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        className={styles.copyButton}
        aria-label={`Copy ${label}`}
        onClick={() => void copy()}
      >
        {copied ? <CheckIcon aria-hidden /> : <CopyIcon aria-hidden />}
        <span className="sr-only" role="status">{copied ? `${label} copied.` : ""}</span>
      </Button>
    </div>
  );
}

function Tooltip({ content, children, title, side, sideOffset = 8, className, contentClassName, nativeButton = true, trigger: triggerKind = "action", forceOpen, followCursor, onOpenChange }: TooltipProps) {
  const generatedId = useId();
  const childProps = children.props as HTMLAttributes<HTMLElement> & { id?: string; href?: unknown };
  const isLink = childProps.href !== undefined;
  const id = childProps.id ?? generatedId;
  const bodyId = `${id}-content`;
  const depth = useContext(TooltipDepthContext);
  const ambientGroup = useContext(TooltipGroupContext);
  const localGroup = useTooltipGroup();
  const groupRef = ambientGroup ?? localGroup;
  const portalContainer = useContext(TooltipPortalContainerContext);
  const shape = useShape();
  const [handle] = useState(() => PopoverPrimitive.createHandle());
  const [phase, setPhase] = useState<TooltipPhase>("closed");
  const triggerRef = useRef<HTMLElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const focusPopup = useRef(false);
  const skipRestoredFocus = useRef(false);
  const open = forceOpen ?? phase !== "closed";
  const locked = forceOpen === true || phase === "locked";
  const followOffset = useMotionValue(0);
  const announcedOpen = useRef(false);
  const callback = useRef(onOpenChange);
  callback.current = onOpenChange;

  useEffect(() => {
    if (announcedOpen.current !== open) callback.current?.(open);
    announcedOpen.current = open;
  }, [open]);

  useEffect(() => {
    if (forceOpen === true) handle.open(id);
    if (forceOpen === false) handle.close();
  }, [forceOpen, handle, id]);

  useEffect(() => {
    if (!open || locked) return;
    const timer = window.setTimeout(() => {
      if (document.visibilityState === "visible" && (triggerRef.current?.matches(":hover") || popupRef.current?.matches(":hover"))) handle.open(id);
      else handle.close();
    }, TOOLTIP_LOCK_DELAY);
    return () => window.clearTimeout(timer);
  }, [open, locked, handle, id]);

  useEffect(() => {
    if (locked && focusPopup.current) {
      focusPopup.current = false;
      popupRef.current?.focus({ preventScroll: true });
    }
  }, [locked]);

  useEffect(() => {
    if (!open) return;
    const close = () => handle.close();
    const hide = () => { if (document.hidden) close(); };
    window.addEventListener("blur", close);
    document.addEventListener("visibilitychange", hide);
    return () => {
      window.removeEventListener("blur", close);
      document.removeEventListener("visibilitychange", hide);
    };
  }, [open, handle]);

  useEffect(() => () => { if (groupRef.get()?.id === id) groupRef.set(null); }, [groupRef, id]);

  function lock(focus = false) {
    focusPopup.current = focus;
    handle.open(id);
    // Opening an already locked panel does not rerun the effect above.
    if (locked && focus) popupRef.current?.focus({ preventScroll: true });
  }

  function middleDown(event: React.MouseEvent) {
    if (event.button !== 1 || !open || (event.target instanceof Element && event.target.closest("a[href]"))) return;
    event.preventDefault();
    event.stopPropagation();
    lock();
  }

  function movePointer(event: React.PointerEvent) {
    if (event.pointerType !== "mouse" || !followCursor || locked) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const desired = followCursor === "y" ? event.clientY - (rect.top + rect.height / 2) : event.clientX - (rect.left + rect.width / 2);
    const popup = popupRef.current?.getBoundingClientRect();
    if (!popup) { followOffset.set(desired); return; }
    const start = (followCursor === "y" ? popup.top : popup.left) - followOffset.get();
    const size = followCursor === "y" ? popup.height : popup.width;
    const viewport = followCursor === "y" ? window.innerHeight : window.innerWidth;
    followOffset.set(Math.max(12 - start, Math.min(desired, viewport - size - start - 12)));
  }

  const isTerm = triggerKind === "term" && !isLink;
  return (
    <Popover
      handle={handle}
      open={open}
      triggerId={id}
      onOpenChange={(nextOpen, details) => {
        if (nextOpen && forceOpen === false) { details.cancel(); return; }
        if (details.reason === "trigger-press") {
          if (!isTerm) { details.cancel(); handle.close(); return; }
          details.cancel();
          const keyboard = (details.event as MouseEvent).detail === 0;
          if (!locked || keyboard) lock(keyboard);
          else handle.close();
          return;
        }
        if (!nextOpen && details.reason === "trigger-hover" && locked) { details.cancel(); return; }
        if (nextOpen) {
          const current = groupRef.get();
          if (current && current.id !== id) {
            if (current.locked && depth === 0 && details.reason === "trigger-hover") { details.cancel(); return; }
            current.close();
          }
          const nextPhase = details.reason === "trigger-hover" ? "preview" : "locked";
          groupRef.set({ id, locked: nextPhase === "locked", close: () => handle.close() });
          setPhase(nextPhase);
        } else {
          if (groupRef.get()?.id === id) groupRef.set(null);
          if (details.reason === "escape-key" || details.reason === "close-press") skipRestoredFocus.current = true;
          focusPopup.current = false;
          setPhase("closed");
        }
      }}
    >
      <PopoverTrigger
        handle={handle}
        id={id}
        ref={(element) => { triggerRef.current = element; }}
        render={<TooltipTarget element={children} isTerm={isTerm} />}
        nativeButton={nativeButton}
        openOnHover
        delay={TOOLTIP_SHOW_DELAY}
        closeDelay={250}
        data-slot="tooltip-trigger"
        data-tooltip-phase={open ? (locked ? "locked" : "preview") : "closed"}
        className={triggerKind === "term" ? styles.term : undefined}
        role={isTerm && !nativeButton ? "button" : childProps.role}
        aria-haspopup={isTerm ? "dialog" : childProps["aria-haspopup"]}
        aria-expanded={isTerm ? open : childProps["aria-expanded"]}
        aria-controls={isTerm && open ? `${id}-popup` : childProps["aria-controls"]}
        aria-describedby={[childProps["aria-describedby"], open ? bodyId : null].filter(Boolean).join(" ") || undefined}
        tabIndex={childProps.tabIndex}
        onFocus={(event) => {
          if (skipRestoredFocus.current) { skipRestoredFocus.current = false; return; }
          if (event.target === event.currentTarget && event.currentTarget.matches(":focus-visible") && !locked) lock();
        }}
        onKeyDownCapture={(event) => {
          if (!isTerm && (event.key === "Enter" || event.key === " ")) handle.close();
          if (event.key !== "F2" || event.target !== event.currentTarget) return;
          event.preventDefault();
          event.stopPropagation();
          lock(true);
        }}
        onKeyDown={(event) => {
          if (isTerm && (event.key === "Enter" || event.key === " ")) event.stopPropagation();
        }}
        onClick={(event) => { if (isTerm) event.stopPropagation(); }}
        onPointerEnter={followCursor ? movePointer : undefined}
        onPointerMove={followCursor ? movePointer : undefined}
        onPointerDownCapture={(event) => {
          if (!isTerm && event.button === 0) handle.close();
          middleDown(event);
        }}
        onAuxClick={middleDown}
      />
      <PopoverPrimitive.Portal container={portalContainer ?? undefined}>
        <PopoverPrimitive.Positioner anchor={depth ? () => triggerRef.current?.closest('[data-slot="tooltip-content"]') ?? null : undefined} side={side ?? (depth ? "right" : "top")} sideOffset={sideOffset} align={title ? "start" : "center"} collisionPadding={12} className={cn("isolate z-50", contentClassName)}>
          <motion.div style={followCursor === "y" ? { y: followOffset } : followCursor === "x" ? { x: followOffset } : undefined}>
            <PopoverPrimitive.Popup
              id={`${id}-popup`}
              ref={popupRef}
              data-slot="tooltip-content"
              data-phase={locked ? "locked" : "preview"}
              data-depth={depth}
              className={cn(styles.popup, shape.bg, title && styles.rich, className)}
              style={timing}
              initialFocus={false}
              finalFocus={(type) => {
                if (type !== "keyboard") return false;
                // Base UI restores HTML triggers only; chart marks are SVG.
                if (triggerRef.current instanceof SVGElement) {
                  triggerRef.current.focus({ preventScroll: true });
                  return false;
                }
                return triggerRef.current;
              }}
              onMouseDown={middleDown}
              onAuxClick={middleDown}
              onClick={(event) => event.stopPropagation()}
            >
              <div className={cn(styles.heading, !title && styles.headingCompact)}>
                <PopoverTitle className={title ? styles.title : "sr-only"}>{title ?? "Additional information"}</PopoverTitle>
                <PopoverPrimitive.Close aria-label={title ? `Close ${title.toLowerCase()}` : "Close tooltip"} className={styles.close}><XIcon size={14} /></PopoverPrimitive.Close>
              </div>
              <div id={bodyId} className={styles.body} inert={!locked}>
                <TooltipDepthContext.Provider value={depth + 1}><TooltipProvider>{content}</TooltipProvider></TooltipDepthContext.Provider>
              </div>
              <div className={styles.footer}>
                <button type="button" className={styles.lockButton} onClick={() => locked ? handle.close() : lock()}>
                  <TooltipLockIcon locked={locked} /><span aria-live="polite">{locked ? "Locked · dismiss" : "Hold to lock"}</span>
                </button>
                <kbd className={styles.key}>{locked ? "Esc" : "F2"}</kbd>
              </div>
              <div className={styles.progress} aria-hidden="true"><div /></div>
            </PopoverPrimitive.Popup>
          </motion.div>
        </PopoverPrimitive.Positioner>
      </PopoverPrimitive.Portal>
    </Popover>
  );
}

export { Tooltip, TooltipCopyValue, TooltipProvider, TooltipPortalContainer };
export type { TooltipProps, TooltipProviderProps, TooltipSide };
